import type { VendorPriceChangeFlag } from '../api/types'

/**
 * Client-side filters for the review queue. The page loads the whole
 * active queue once (open + reviewing, both directions) and narrows it
 * here, so counts, grouping and sorting always cover every row.
 */

export type DirectionFilter = 'increase' | 'decrease' | 'all'
export type StatusFilter = 'active' | 'open' | 'reviewing'
export type MinPctFilter = 0 | 5 | 10 | 25
export type DetectedFilter = 'any' | 7 | 30 | 90

export interface QueueFilters {
  direction: DirectionFilter
  status: StatusFilter
  /** Vendor names; empty = every vendor. */
  vendors: string[]
  minPct: MinPctFilter
  detectedWithinDays: DetectedFilter
  onlyPoCollisions: boolean
}

export const DEFAULT_QUEUE_FILTERS: QueueFilters = {
  direction: 'increase',
  status: 'active',
  vendors: [],
  minPct: 0,
  detectedWithinDays: 'any',
  onlyPoCollisions: false,
}

/** Filters that differ from the defaults (drives the rail badge). */
export function countActiveFilters(f: QueueFilters): number {
  let n = 0
  if (f.direction !== DEFAULT_QUEUE_FILTERS.direction) n += 1
  if (f.status !== 'active') n += 1
  if (f.vendors.length) n += 1
  if (f.minPct) n += 1
  if (f.detectedWithinDays !== 'any') n += 1
  if (f.onlyPoCollisions) n += 1
  return n
}

export function vendorOf(f: VendorPriceChangeFlag): string {
  return f.vendor_name || `Vendor #${f.vendor_id}`
}

export function productOf(f: VendorPriceChangeFlag): string {
  return f.product_name || `Product #${f.product_tmpl_id}`
}

export function tierOf(f: VendorPriceChangeFlag): string {
  return f.tier_label || f.tier_key
}

const DAY_MS = 86_400_000

export function applyQueueFilters(
  flags: readonly VendorPriceChangeFlag[],
  f: QueueFilters,
  search: string,
  now: number = Date.now(),
): VendorPriceChangeFlag[] {
  const needle = search.trim().toLowerCase()
  const vendors = f.vendors.length ? new Set(f.vendors) : null
  const cutoff = f.detectedWithinDays === 'any' ? null : now - f.detectedWithinDays * DAY_MS
  return flags.filter((flag) => {
    if (f.direction !== 'all' && flag.direction !== f.direction) return false
    if (f.status !== 'active' && flag.status !== f.status) return false
    if (vendors && !vendors.has(vendorOf(flag))) return false
    if (f.minPct && Math.abs(flag.pct_change) < f.minPct) return false
    if (cutoff !== null && new Date(flag.detected_at).getTime() < cutoff) return false
    if (f.onlyPoCollisions && !flag.open_po_collision_count) return false
    if (needle) {
      const hay = `${vendorOf(flag)} ${productOf(flag)} ${tierOf(flag)} ${flag.tier_key}`.toLowerCase()
      if (!hay.includes(needle)) return false
    }
    return true
  })
}

/** Vendors with their row counts under every filter except Vendor itself,
 *  so the vendor list never hides the vendors you could still add. */
export function vendorCounts(
  flags: readonly VendorPriceChangeFlag[],
  f: QueueFilters,
  search: string,
  now?: number,
): { name: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const flag of applyQueueFilters(flags, { ...f, vendors: [] }, search, now)) {
    const v = vendorOf(flag)
    counts.set(v, (counts.get(v) ?? 0) + 1)
  }
  for (const v of f.vendors) if (!counts.has(v)) counts.set(v, 0)
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

/** Rows per direction under every other filter + search, so the
 *  Direction control can say what each choice would show. */
export function directionCounts(
  flags: readonly VendorPriceChangeFlag[],
  f: QueueFilters,
  search: string,
  now?: number,
): Record<DirectionFilter, number> {
  const rows = applyQueueFilters(flags, { ...f, direction: 'all' }, search, now)
  const increase = rows.filter((r) => r.direction === 'increase').length
  return { increase, decrease: rows.length - increase, all: rows.length }
}

export function formatPct(v: number): string {
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`
}
